import React from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Switch } from '@mui/material';

import * as S from 'commons/components/Table/Table.styles';
import { IBinCommodity, User } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { UserRolesEnum } from 'commons/types/user.roles.enum';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import { selectUser } from 'redux-stores/features/authSlice';

const rowTitles = (user: User): RowTitleProps[] => {
  return [
    { title: 'Name' },
    {
      title: user.roles.includes(UserRolesEnum.SUPER_ADMIN)
        ? 'Is Enabled'
        : 'Status',
    },
  ];
};

const Row: React.FC<{
  user: User;
  data: IBinCommodity;
  index: number;
  onClickEdit: (commodity: IBinCommodity) => void;
  onClickToggleStatus: (commodity: IBinCommodity) => void;
  onClickDelete: (commodity: IBinCommodity) => void;
}> = ({
  user,
  data,
  index,
  onClickEdit,
  onClickToggleStatus,
  onClickDelete,
}) => {
  const isSuperAdmin = user.roles.includes(UserRolesEnum.SUPER_ADMIN);

  return (
    <S.TableRow>
      <S.TableColumn
        style={{ width: '40%' }}
        data-testid={`listCommodityName${index + 1}`}
      >
        {data.name}
      </S.TableColumn>
      <S.TableColumn
        style={{ width: '20%' }}
        data-testid={`listCommodityStatus${index + 1}`}
      >
        {isSuperAdmin ? (
          <Switch
            data-testid={`switchCommodityStatus${index + 1}`}
            checked={data.is_enabled}
            onChange={() => onClickToggleStatus(data)}
          />
        ) : data.is_enabled ? (
          'Enabled'
        ) : (
          'Disabled'
        )}
      </S.TableColumn>
      {isSuperAdmin && (
        <S.TableColumn
          style={{ width: '40%', textAlign: 'right', paddingLeft: '12px' }}
          data-testid={`listCommodityButtons${index + 1}`}
        >
          <Button
            data-testid={`buttonCommodityEdit${index + 1}`}
            onClick={() => onClickEdit(data)}
            variant="outlined"
          >
            Edit
          </Button>
          <Button
            data-testid={`buttonCommodityDelete${index + 1}`}
            onClick={() => onClickDelete(data)}
            variant="outlined"
            color="error"
            style={{ marginLeft: '8px' }}
          >
            Delete
          </Button>
        </S.TableColumn>
      )}
    </S.TableRow>
  );
};

const CommodityTable: React.FC<{
  data: IBinCommodity[];
  onClickCreate: () => void;
  onClickEdit: (commodity: IBinCommodity) => void;
  onClickToggleStatus: (commodity: IBinCommodity) => void;
  onClickDelete: (commodity: IBinCommodity) => void;
}> = ({
  data = [],
  onClickCreate,
  onClickEdit,
  onClickToggleStatus,
  onClickDelete,
}): React.ReactNode => {
  const user = useSelector(selectUser);
  return (
    <>
      <Box sx={{ marginBottom: '24px' }}>
        <Button onClick={onClickCreate} variant="outlined">
          Create New Commodity
        </Button>
      </Box>
      <DataTable rowTitles={rowTitles(user)} hideCheckbox>
        <S.TableBody>
          {data.map((item, index) => (
            <Row
              user={user}
              key={item._id}
              data={item}
              index={index}
              onClickEdit={onClickEdit}
              onClickToggleStatus={onClickToggleStatus}
              onClickDelete={onClickDelete}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {data.length > 0 && (
        <Box sx={{ marginTop: '24px' }}>
          <Button onClick={onClickCreate} variant="outlined">
            Create New Commodity
          </Button>
        </Box>
      )}
    </>
  );
};

export default CommodityTable;
